import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  finalize,
  map,
  merge,
  of,
  scan,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import {
  ContentFilters,
  Feed,
  ObjectDefinition,
  Post,
  QueryBuilderContext,
} from '../../../core/models/core.models';
import { QueryHistoryRecord } from '../../../core/services/feed.service';
import { FeedApiService } from 'src/app/api/services/feed-api.service';
import {
  FeedSettingsService,
  PropertyViewConfig,
} from '../../../core/services/feed-settings.service';
import {
  FormArray,
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormOption, FormsService } from '../../../core/services/forms.service';
import {
  NomConnectorObjectType,
  NomObjectClass,
  NomObjectTypes,
} from '../../../core/constants';
import { selectUser } from '../../../core/state';
import { injectFeedHistoryQuery } from 'src/app/api/queries/utils-log.queries';

import { AppState } from 'src/app/core/state/app.state';
import { EditorFormEntity } from '../../../core/models/form.models';
import { GeneralDynamicFormComponent } from '../general-dynamic-form/general-dynamic-form.component';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ItemsProvider } from 'src/app/shared/components/dropdown-checkbox/dropdown-checkbox.component';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from '../../../core/services/nomenclature.service';
import { QueryEditorComponent } from '../query-editor/query-editor.component';
import { QueryHintOverlayService } from '@services/query-hint-overlay.service';
import { SidebarService } from '../../../core/services/sidebar.service';
import { Store } from '@ngrx/store';
import { injectCompanyContentPlatformsQuery } from 'src/app/api/queries/company.queries';
import { socialLogoPath } from 'src/app/shared/utils/social-logo.utils';
import { ToastService } from '@services/toast.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  NgClass,
  NgTemplateOutlet,
  AsyncPipe,
  DecimalPipe,
} from '@angular/common';
import { DropdownCheckboxComponent } from '../dropdown-checkbox/dropdown-checkbox.component';
import { SuiTagsComponent } from '@sui/tags';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SuiButtonComponent } from '@sui/button';
import { NgpPopoverTrigger, NgpPopover } from 'ng-primitives/popover';
import { SuiPopoverTriggerDirective, SuiPopoverDirective } from '@sui/popover';
import { NgpFocusTrap } from 'ng-primitives/focus-trap';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { DataObjectListComponent } from '../../../objects/components/dataobject-list/dataobject-list.component';
import { QueryHistoryViewerComponent } from './query-history-viewer/query-history-viewer.component';
import { RemovedItemsComponent } from './removed-items/removed-items.component';
import { SuiTooltipDirective } from '@sui/tooltip';
import { LoadingComponent } from '../loading/loading.component';
import { PostCardComponent } from '../../../feed/components/post-card/post-card.component';
import { LoadingCardComponent } from '../loading-card/loading-card.component';
import { FeedQueryHintsPopupComponent } from '../feed-query-hints-popup/feed-query-hints-popup.component';
import { TagsOptionsPipe } from '../../pipes/tags-options.pipe';

export interface JoinOption {
  joinType: 'include' | 'exclude';
  propertyUri: string;
  name: string;
  queryField?: string;
  joinOperator?: 'AND' | 'OR' | 'AND NOT';
  items?: string[];
  selected?: boolean;
  autocompleteObservable?: Observable<any>;
  required?: boolean; // if the join option is required
  icon?: IconProp;
}
export interface SearchGroup {
  title?: string; // title of the group
  joinType: 'or' | 'and'; // join type of the group, or or and
  joinOption: JoinOption; // join option for the group // exclude or include
  type: 'term' | 'filter'; // type of the group, term or filter
  hasError?: boolean; // used to show error message
  objectTypeIds?: number[]; // search group is only applicable to these object types
  formControlName?: string; // name of the form control
  items?: {
    value: any;
    name: string;
    image?: string;
    icon?: IconProp;
  }[];
  serverFiltering?: boolean; // whether the options are filtered on server side
  itemsProvider?: ItemsProvider; // function to provide items for the group
  itemsValueProvider?: (ids: any[]) => Observable<FormOption[]>; // function to provide items for the group by ids
  options$?: Observable<FormOption[]>; // observable to provide items for the group
  required?: boolean; // if the group is required
  value: any[]; // value of the group with the selected values
  icon?: IconProp;
}

export interface SearchBuilderData {
  combined_feeds: Array<string>;
  filters: any;
  object_type_id: number;
  query_created_from: string;
  social_facebook_comments_status: boolean;
  social_source_id: Array<number>;
  query_text: string;
}

export type BuilderType =
  | 'editor'
  | 'builder'
  | 'history'
  | 'combined'
  | 'excluded-posts';

export interface TabConfig {
  key: BuilderType; // Unique identifier for the tab (e.g., 'builder', 'editor')
  label: string; // Translation key or plain label for the tab
  icon: IconProp; // FontAwesome icon tuple, e.g., ['fas', 'search']
  show: boolean; // Whether the tab should be displayed
}

@Component({
  selector: 'app-search-builder',
  templateUrl: './search-builder.component.html',
  styleUrls: ['./search-builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    DropdownCheckboxComponent,
    SuiTagsComponent,
    FaIconComponent,
    NgTemplateOutlet,
    SuiButtonComponent,
    NgpPopoverTrigger,
    SuiPopoverTriggerDirective,
    NgpFocusTrap,
    NgpPopover,
    SuiPopoverDirective,
    SuiCheckboxComponent,
    QueryEditorComponent,
    forwardRef(() => DataObjectListComponent),
    QueryHistoryViewerComponent,
    RemovedItemsComponent,
    SuiTooltipDirective,
    LoadingComponent,
    forwardRef(() => PostCardComponent),
    LoadingCardComponent,
    FeedQueryHintsPopupComponent,
    AsyncPipe,
    DecimalPipe,
    TranslatePipe,
    TagsOptionsPipe,
  ],
})
export class SearchBuilderComponent
  extends GeneralDynamicFormComponent
  implements OnChanges, OnDestroy
{
  @ViewChild('queryEditor', { static: false })
  queryEditor: QueryEditorComponent;

  @Input() feed: Feed; // if feed is passed then it will be in edit mode
  @Input() objectTypeId: NomObjectTypes; // objectTypeId is for creating new feed
  @Input() query: string; // query for initial query
  @Input() showPreviewPane: boolean = false; // show query editor or not
  @Input() ctrlEnterExtension = null;

  NomObjectTypes = NomObjectTypes;
  NomConnectorObjectType = NomConnectorObjectType;

  @Output() componentDataChange: EventEmitter<any> = new EventEmitter();
  @Output() builderChanged = new EventEmitter<BuilderType>();

  // Set by loadFeedHistory() once the feed is known.
  private readonly historyFeedId = signal<string | null>(null);
  private readonly feedHistoryQuery = injectFeedHistoryQuery(
    this.historyFeedId,
  );

  historyRecords$: Observable<Array<QueryHistoryRecord>> = toObservable(
    computed(() => this.feedHistoryQuery.data() ?? []),
  );
  editorFormEntityName = EditorFormEntity.Feed;
  private subscription = new Subscription();

  postsLoading = false;
  posts: Array<Post> = [];
  postsPreviewCount: number = null;
  postLoaded = false;
  isQueryEditorReadOnly = false; // Flag to control read-only mode for the query editor
  componentId: string = 'search-builder';

  // Subject that we’ll push search terms into
  searchTerms = new Subject<string>();

  // Debounced trigger for auto-preview when the user edits the builder or advanced query
  private previewTrigger$ = new Subject<void>();
  private static readonly PREVIEW_DEBOUNCE_MS = 600;
  // if the feed is combined feed then we store the combined feed ids in this map for
  // quick access to the feeds in datasource list component
  combinedFeedIds: Map<string, ObjectDefinition> = new Map();

  builderType: BuilderType = 'builder';
  termJoinOptions: Array<JoinOption> = [
    {
      joinType: 'include',
      propertyUri: 'post_content_search',
      name: this.translateService.instant(
        'search-builder.search-group.include-any-of-the-terms',
      ),
      queryField: 'content',
      joinOperator: 'OR',
      required: true,
      icon: ['fas', 'search'],
    },
    {
      joinType: 'include',
      propertyUri: 'post_content_search_required',

      name: this.translateService.instant(
        'search-builder.search-group.include-all-of-the-terms',
      ),
      queryField: 'content',
      joinOperator: 'AND',
      icon: ['fas', 'filter'],
    },

    {
      joinType: 'exclude',
      propertyUri: 'post_content_search_required',
      name: this.translateService.instant(
        'search-builder.search-group.exclude-all-of-the-terms',
      ),
      queryField: 'content',
      joinOperator: 'AND',
      icon: ['fas', 'minus-circle'],
    },
    {
      joinType: 'exclude',
      propertyUri: 'post_content_search',
      name: this.translateService.instant(
        'search-builder.search-group.exclude-any-of-the-terms',
      ),
      joinOperator: 'AND',
      icon: ['fas', 'ban'],
    },
  ];

  previewViewConfig: PropertyViewConfig[] = [];

  termSearchGroups: SearchGroup[] = []; // build dynamically by the user
  filterSearchGroups: SearchGroup[] = [];

  availableFilterJoinOptions: JoinOption[] = [];
  availableTermJoinOptions: JoinOption[] = [];

  /** The platform picker, served and gated by the backend (SEN-6208 slice 1c, WAP-2046). */
  protected readonly contentPlatforms = injectCompanyContentPlatformsQuery();
  protected readonly socialLogoPath = socialLogoPath;

  availableTermSearchGroups = this.termJoinOptions.map(
    (joinOption): SearchGroup => ({
      type: 'term',
      joinType: 'and',
      joinOption,
      items: [],
      value: [],
      required: joinOption.required || false,
      icon: joinOption.icon,
    }),
  );

  availableFilterSearchGroups: SearchGroup[] = [
    // Sentiment
    ...this.makeFilterGroupPair({
      titleKey: 'search-builder.search-group.sentiment',
      propertyUri: 'post_sentiment_id',
      objectTypeIds: [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.SEARCH_DIMENSION,
      ],

      itemsProvider: this.nomService.makeNomenclatureItemsProvider('sentiment'),
      itemsValueProvider: (ids: any[]) =>
        this.nomService.fetchNomencatureItemsByIdsAsFormOptions(
          'sentiment',
          ids,
        ),
      includeTranslateKey:
        'search-builder.search-group.include-sentiment-values',
      excludeTranslateKey:
        'search-builder.search-group.exclude-sentiment-values',
    }),

    // Source global rank
    ...this.makeFilterGroupPair({
      titleKey: 'search-builder.search-group.source-global-rank',
      propertyUri: 'post_source_rank_global',
      objectTypeIds: [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.SEARCH_DIMENSION,
      ],
      itemsProvider:
        this.nomService.makeNomenclatureItemsProvider('source_rank'),
      itemsValueProvider: (ids: any[]) =>
        this.nomService.fetchNomencatureItemsByIdsAsFormOptions(
          'source_rank',
          ids,
        ),
      includeTranslateKey:
        'search-builder.search-group.include-source-global-ranks',
      excludeTranslateKey:
        'search-builder.search-group.exclude-source-global-ranks',
    }),

    // Source country rank
    ...this.makeFilterGroupPair({
      titleKey: 'search-builder.search-group.source-country-rank',
      propertyUri: 'post_source_rank_country',
      objectTypeIds: [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.SEARCH_DIMENSION,
      ],
      itemsProvider:
        this.nomService.makeNomenclatureItemsProvider('source_rank'),
      itemsValueProvider: (ids: any[]) =>
        this.nomService.fetchNomencatureItemsByIdsAsFormOptions(
          'source_rank',
          ids,
        ),
      includeTranslateKey:
        'search-builder.search-group.include-source-country-ranks',
      excludeTranslateKey:
        'search-builder.search-group.exclude-source-country-ranks',
    }),

    // Language
    ...this.makeFilterGroupPair({
      titleKey: 'search-builder.search-group.language',
      propertyUri: 'post_language_id',
      objectTypeIds: [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.BROADCAST_FEED,
        NomObjectTypes.SEARCH_DIMENSION,
      ],
      itemsProvider: this.nomService.makeNomenclatureItemsProvider('language'),
      itemsValueProvider: (ids: any[]) =>
        this.nomService.fetchNomencatureItemsByIdsAsFormOptions(
          'language',
          ids,
        ),
      includeTranslateKey: 'search-builder.search-group.include-languages',
      excludeTranslateKey: 'search-builder.search-group.exclude-languages',
      serverFiltering: true,
    }),

    // Country
    ...this.makeFilterGroupPair({
      titleKey: 'search-builder.search-group.country',
      propertyUri: 'post_section_country_id',
      objectTypeIds: [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.SOCIAL_FEED,
        NomObjectTypes.BROADCAST_FEED,
      ],
      itemsProvider: this.nomService.makeNomenclatureItemsProvider('location'),
      itemsValueProvider: (ids: any[]) =>
        this.nomService.fetchNomencatureItemsByIdsAsFormOptions(
          'location',
          ids,
        ),
      includeTranslateKey:
        'search-builder.search-group.include-source-countries',
      excludeTranslateKey:
        'search-builder.search-group.exclude-source-countries',
      serverFiltering: true,
    }),

    // Source (online)
    ...this.makeFilterGroupPair({
      titleKey: 'search-builder.search-group.source',
      propertyUri: 'post_source_id',
      objectTypeIds: [NomObjectTypes.ONLINE_FEED, NomObjectTypes.STORY_FEED],
      itemsProvider: this.nomService.makeNomenclatureItemsProvider('source'),
      itemsValueProvider: (ids: any[]) =>
        this.nomService.fetchNomencatureItemsByIdsAsFormOptions('source', ids),
      includeTranslateKey: 'search-builder.search-group.include-sources',
      excludeTranslateKey: 'search-builder.search-group.exclude-sources',
      serverFiltering: true,
    }),

    // Content type
    ...this.makeFilterGroupPair({
      titleKey: 'search-builder.search-group.content-type',
      propertyUri: 'post_content_type',
      objectTypeIds: [
        NomObjectTypes.ONLINE_FEED,
        NomObjectTypes.STORY_FEED,
        NomObjectTypes.BROADCAST_FEED,
      ],
      itemsProvider:
        this.nomService.makeNomenclatureItemsProvider('content_type'),
      itemsValueProvider: (ids: any[]) =>
        this.nomService.fetchNomencatureItemsByIdsAsFormOptions(
          'content_type',
          ids,
        ),
      includeTranslateKey: 'search-builder.search-group.include-content-types',
      excludeTranslateKey: 'search-builder.search-group.exclude-content-types',
      serverFiltering: true,
    }),
  ];

  createAutocompleteFunction(nomService, propertyUri) {
    return (text: string): Observable<any> => {
      return of(text).pipe(
        debounceTime(100), // Wait for 100ms pause in events
        switchMap((term) =>
          term ? nomService.getAutocompleteResults(term, propertyUri) : of([]),
        ),
        catchError(() => of([])), // Handle errors by returning an empty array
      );
    };
  }

  protected searchBuilderEnabled: boolean = true;
  protected toastService = inject(ToastService);
  tabs: TabConfig[];

  visibleTabs: TabConfig[] = [];

  constructor(
    protected nomService: NomenclatureService,
    protected formService: FormsService,
    protected feedApi: FeedApiService,
    protected translateService: TranslateService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
    private sidebarService: SidebarService,
    public queryHintsOverlayService: QueryHintOverlayService,
    private feedSettingsService: FeedSettingsService,
  ) {
    super(formService, store, logger);

    this.tabs = [
      {
        key: 'combined',
        label: 'search-builder.tab.combined',
        icon: ['fas', 'layer-group'],
        show: this.objectTypeId === NomObjectTypes.COMBINED_FEED,
      },
      {
        key: 'builder',
        label: 'search-builder.tab.builder',
        icon: ['fas', 'search'],
        show: this.searchBuilderEnabled,
      },
      {
        key: 'editor',
        label: 'search-builder.tab.advanced',
        icon: ['fas', 'search-plus'],
        show: true,
      },
      {
        key: 'history',
        label: 'search-builder.tab.history',
        icon: ['fas', 'clock-rotate-left'],
        show: !!this.feed,
      },
      {
        key: 'excluded-posts',
        label: 'search-builder.tab.excluded-posts',
        icon: ['fas', 'trash'],
        show: true,
      },
    ];

    // this.countryAutocompleteObservable = this.createAutocompleteFunction(
    //   this.nomService,
    //   'post_section_country_id',
    // );
  }

  estimateVisibleTabs() {
    this.visibleTabs = this.tabs.filter((tab) => {
      switch (tab.key) {
        case 'builder':
          return this.searchBuilderEnabled;
        case 'history':
          return (
            !!this.feed &&
            this.feed?.object_type_id !== NomObjectTypes.COMBINED_FEED
          );
        case 'combined':
          return this.objectTypeId === NomObjectTypes.COMBINED_FEED;
        case 'excluded-posts':
          return (
            !!this.feed && this.objectTypeId !== NomObjectTypes.SEARCH_DIMENSION
          );
        default:
          return true; // editor is always visible
      }
    });
  }

  /**
   * Updates the search groups based on the provided feed.
   * @param feed The feed object used to build the search groups.
   */
  updateSearchGroupsFromFeed(feed: Feed, reset: boolean = true): void {
    if (reset) {
      this.termSearchGroups = [];
      this.resetStaticGroupValues();
    }

    if (!this.formGroup) {
      this.logger.warn(
        'Form group is not initialized, cannot update search groups from feed.',
      );
      return;
    }

    const queryText = feed.query == null ? '' : String(feed.query);

    const newFormValue = {
      combined_feeds: feed.combined_feeds,
      query_text: queryText,
      query_created_from: feed.query_created_from,
      object_type_id: feed.object_type_id,
    };

    this.logger.debug(
      'Updating form from feed:',
      feed,
      'New form value:',
      newFormValue,
    );

    this.formGroup.patchValue(newFormValue);
    // Manually patch the FormArray

    const sourcesArray = this.formGroup.get(
      'post_social_source_id',
    ) as FormArray;
    if (sourcesArray !== null) {
      sourcesArray.clear();
      feed.social_sources.forEach((source) => {
        sourcesArray.push(new FormControl(source));
      });
    }

    if (feed.query_created_from && feed.query_created_from == 'editor') {
      this.searchBuilderEnabled = false;
    }
    this.queryEditor?.setQuery(queryText);

    const termGroups = ['post_content_search', 'post_content_search_required'];
    const filterGroups = [
      'post_source_rank_global',
      'post_source_rank_country',
      'post_sentiment_id',
      'post_language_id',
      'post_section_country_id',
      'post_source_id',
      'post_content_type',
    ];

    if (reset) {
      this.feedApi
        .getFeedQueryContexts(feed.id)
        .pipe(
          take(1),
          tap((queryContexts) => {
            let addedFilterGroupsCount = 0;
            let addedTermGroupsCount = 0;

            Object.entries(queryContexts).forEach(([_, queryContext]) => {
              if (filterGroups.includes(queryContext.property_uri)) {
                addedFilterGroupsCount +=
                  this.addFilterSearchGroupFromQueryContext(queryContext);
              } else if (termGroups.includes(queryContext.property_uri)) {
                addedTermGroupsCount +=
                  this.addTermSearchGroupFromQueryContext(queryContext);
              }
            });

            if (addedTermGroupsCount === 0) {
              this.addDefaultSearchGroup();
            }

            this.updateFieldValidatorsByBuilderType(this.builderType);

            this.logger.debug('form group populated', this.formGroup.value);
          }),
        )
        .subscribe();
    }
  }

  private resetStaticGroupValues(): void {
    this.filterSearchGroups.forEach((group) => (group.value = []));
  }

  private addTermSearchGroupFromQueryContext(
    queryContext: QueryBuilderContext,
  ): number {
    let count = 0;
    queryContext.methods.forEach((method) => {
      const values = method.items?.map((item) => item.text);
      // const items = method.items?.map((item) => ({
      //   value: item.raw_value,
      //   name: item.text,
      // }));

      const propertyUrl = queryContext.property_uri;
      const methodName = method.name as 'include' | 'exclude';

      const existingGroup = this.availableTermSearchGroups.find(
        (group) =>
          group.joinOption.propertyUri === propertyUrl &&
          group.joinOption.joinType === methodName,
      );

      this.logger.debug(
        `Adding term search group for method: ${method.name}, values: ${JSON.stringify(values)}`,
      );

      if (values.length > 0) {
        this.addTermSearchGroup(
          {
            joinType: method.name as 'include' | 'exclude',
            propertyUri: queryContext.property_uri,
            name: existingGroup.joinOption.name,
            joinOperator: 'AND',
            items: values,
          },
          values,
        );
        count++;
      }
    });

    return count;
  }

  private addFilterSearchGroupFromQueryContext(
    queryContext: QueryBuilderContext,
  ): number {
    let count = 0;
    queryContext.methods.forEach((method) => {
      const values = method.items.map((item) => item.raw_value);
      if (values.length > 0) {
        this.addFilterSearchGroup(
          {
            joinType: method.name as 'include' | 'exclude',
            propertyUri: queryContext.property_uri,
            name: method.name,
            joinOperator: 'AND',
          },
          values,
        );
        count++;
      }
    });
    return count;
  }

  createFormGroup(): void {
    this.logger.debug(
      'Creating form group for search builder with objectTypeId:',
      this.objectTypeId,
    );

    // * 1. initialize Base Form
    this.initializeBaseForm();

    // * 2. determine the Mode and configure accordingly
    if (this.feed) {
      this.configureEditMode();
    } else {
      this.configureCreateMode();
    }

    // * 3. finalize configuration
    this.finalizeFormConfiguration();
  }

  private initializeBaseForm(): void {
    super.createFormGroup();

    // * add Base Form Controls
    const baseControls: Record<string, FormControl> = {
      query_created_from: new FormControl('builder'),
      object_type_id: new FormControl(this.objectTypeId),
      combined_feeds: new FormControl(),
      query_text: new FormControl(''),
      social_facebook_comments_status: new FormControl(false),
    };

    Object.entries(baseControls).forEach(([key, control]) =>
      this.formGroup.addControl(key, control),
    );

    // * add specific Form Controls for SOCIAL_FEED
    const isSocialFeed =
      this.objectTypeId === NomObjectTypes.SOCIAL_FEED ||
      this.feed?.object_type_id === NomObjectTypes.SOCIAL_FEED;

    if (isSocialFeed) {
      this.formGroup.addControl(
        'post_social_source_id',
        new FormArray([], Validators.required),
      );
    }
  }

  private configureEditMode(): void {
    this.objectTypeId = this.feed.object_type_id;
    this.builderType = this.feed.query_created_from;

    if (this.objectTypeId === NomObjectTypes.COMBINED_FEED) {
      this.handleCombinedFeed(this.feed.combined_feeds);
    }

    this.logger.debug('Initialize buildertype feed data:', this.builderType);
    this.builderChanged.emit(this.builderType);
    this.updateSearchGroupsFromFeed(this.feed);
  }

  private configureCreateMode(): void {
    if (this.query) {
      this.configureCreateModeWithQuery();
    } else if (this.objectTypeId === NomObjectTypes.COMBINED_FEED) {
      this.configureCreateModeCombinedFeed();
    } else {
      this.configureCreateModeRegular();
    }
  }

  private configureCreateModeWithQuery(): void {
    this.builderType = 'editor';
    this.searchBuilderEnabled = false;
    this.estimateVisibleTabs();
  }

  private configureCreateModeCombinedFeed(): void {
    this.handleCombinedFeed();
  }

  private configureCreateModeRegular(): void {
    this.builderType = 'builder';
    this.addDefaultSearchGroup();
  }

  private finalizeFormConfiguration(): void {
    // ! update Form Control with final Builder type
    this.formGroup.get('query_created_from')?.setValue(this.builderType);

    // ! update Validators based on Builder type
    this.updateFieldValidatorsByBuilderType(this.builderType);
  }

  private handleCombinedFeed(combinedFeeds?: string[]): void {
    this.searchBuilderEnabled = false;
    this.builderType = 'combined';
    this.isQueryEditorReadOnly = true; // Make query editor read-only for combined feeds

    combinedFeeds?.forEach((combinedFeedId) => {
      this.combinedFeedIds.set(combinedFeedId, {
        object_id: combinedFeedId,
        object_class_id: NomObjectClass.FEED,
      });
    });

    this.estimateVisibleTabs();
  }

  private addDefaultSearchGroup() {
    this.termSearchGroups = [];
    this.filterSearchGroups = [];
    this.addTermSearchGroup({
      joinType: 'include',
      propertyUri: 'post_content_search',
      icon: ['fas', 'search'],
      name: this.translateService.instant(
        'search-builder.search-group.include-any-of-the-terms',
      ),
      joinOperator: 'OR',
      selected: true,
      required: true,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['objectTypeId'] &&
      changes['objectTypeId'].currentValue &&
      changes['objectTypeId'].previousValue !==
        changes['objectTypeId'].currentValue &&
      !changes['objectTypeId'].firstChange
    ) {
      this.setupAvailableSearchGroups();
      this.createFormGroup();
    }

    if (changes['feed']) {
      const feed = changes['feed'].currentValue;
      if (feed) {
        this.updateSearchGroupsFromFeed(feed, true);
        this.loadFeedHistory();
      }
    }
  }

  ngOnInit() {
    super.ngOnInit();

    this.formGroup = this.buildFormGroup();

    this.setupAvailableSearchGroups();
    this.setupFormGroupOptionProviders();
    this.estimateVisibleTabs();

    this.loadFeedHistory();

    this.setupPreviewAutoTrigger();

    this.store
      .select(selectUser)
      .pipe(
        take(1),
        tap((user) => {
          this.feedSettingsService
            .getFeedConfig(this.objectTypeId, user)
            .then((config) => {
              this.previewViewConfig = config;
            });
        }),
      )
      .subscribe();
  }

  private setupPreviewAutoTrigger(): void {
    if (!this.showPreviewPane) return;

    this.previewTrigger$
      .pipe(
        debounceTime(SearchBuilderComponent.PREVIEW_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.previewQuery());
  }

  private loadFeedHistory() {
    if (this.feed) {
      this.historyFeedId.set(this.feed.id);
    }
  }

  private setupFormGroupOptionProviders() {
    this.availableFilterSearchGroups.forEach((group) => {
      if (group.itemsProvider) {
        const itemsProvider = group.itemsProvider as ItemsProvider;

        // 1) search term stream
        const term$ = new BehaviorSubject<string>('');

        // 2) “load next page” trigger
        const loadMore$ = new Subject<void>();

        // 3) pager state driven by term + loadMore
        const pagerState$ = term$.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          // whenever term changes, rebuild a paging stream from scratch
          switchMap((term) => {
            let pageToken: string | undefined = undefined;

            // inner stream that reacts to resets & loadMore
            return merge(
              of<'reset'>('reset'),
              loadMore$.pipe(map(() => 'more' as const)),
            ).pipe(
              // ensure only one page fetch at a time
              exhaustMap((kind) => {
                // reset pagination on new term
                if (kind === 'reset') pageToken = undefined;

                return itemsProvider(term, { pageToken }).pipe(
                  tap((res) => {
                    pageToken = res.next_page_token;
                  }),
                  map((res) => ({ kind, res })),
                );
              }),
              // accumulate items across pages
              scan(
                (acc, { kind, res }) => ({
                  items:
                    kind === 'reset' ? res.items : acc.items.concat(res.items),
                  next_page_token: res.next_page_token,
                  done: !res.next_page_token,
                }),
                {
                  items: [] as FormOption[],
                  next_page_token: undefined as string | undefined,
                  done: false,
                },
              ),
              shareReplay(1),
            );
          }),
          shareReplay(1),
        );

        // 4) public streams for the component
        group.options$ = pagerState$.pipe(map((s) => s.items));
        (group as any).hasMore$ = pagerState$.pipe(map((s) => !s.done));
        (group as any).loadMore = () => loadMore$.next();
        (group as any)._term$ = term$; // keep your existing hook
      } else if (group.items) {
        group.options$ = of(group.items);
      }
    });
  }

  private setupAvailableSearchGroups() {
    this.availableFilterJoinOptions = [];
    this.availableTermJoinOptions = [];
    this.availableFilterSearchGroups.forEach((group) => {
      if (
        !group.objectTypeIds ||
        group.objectTypeIds.includes(this.objectTypeId)
      ) {
        this.availableFilterJoinOptions.push(group.joinOption);
      }
    });
    this.availableTermSearchGroups.forEach((group) => {
      this.availableTermJoinOptions.push(group.joinOption);
    });

    this.availableFilterJoinOptions = this.availableFilterJoinOptions.sort(
      SearchBuilderComponent.compareJoinOptions,
    );
  }

  private static compareJoinOptions(a: JoinOption, b: JoinOption): number {
    const order = { include: 0, exclude: 1 } as const;

    const byType = order[a.joinType] - order[b.joinType];
    if (byType !== 0) return byType;

    // optional secondary sort (stable and nice in UI)
    return (a.name || '').localeCompare(b.name || '');
  }

  private makeFilterGroupPair(args: {
    titleKey: string;
    propertyUri: string;
    objectTypeIds?: NomObjectTypes[];
    itemsProvider?: ItemsProvider;
    itemsValueProvider?: (ids: any[]) => Observable<FormOption[]>;
    includeTranslateKey?: string;
    excludeTranslateKey?: string;
    serverFiltering?: boolean;
  }) {
    const {
      titleKey,
      propertyUri,
      objectTypeIds,
      itemsProvider,
      itemsValueProvider,
      includeTranslateKey,
      excludeTranslateKey,
      serverFiltering,
    } = args;

    return [
      // INCLUDE
      {
        title: this.translateService.instant(titleKey),
        joinType: 'and' as const,
        type: 'filter' as const,
        objectTypeIds,
        serverFiltering,
        joinOption: {
          propertyUri,
          joinType: 'include' as const,
          name: this.translateService.instant(
            includeTranslateKey ??
              'search-builder.search-group.include-from-any-of-the-values',
          ),
        },
        itemsProvider,
        itemsValueProvider,
        value: [],
      },
      // EXCLUDE
      {
        title: this.translateService.instant(titleKey),
        joinType: 'and' as const,
        type: 'filter' as const,
        objectTypeIds,
        serverFiltering,
        joinOption: {
          propertyUri,
          joinType: 'exclude' as const,
          name: this.translateService.instant(
            excludeTranslateKey ??
              'search-builder.search-group.exclude-from-any-of-the-values',
          ),
          // optional: if you want to be explicit
          // joinOperator: 'AND NOT' as const,
        },
        itemsProvider,
        itemsValueProvider,
        value: [],
      },
    ];
  }

  ngAfterViewInit(): void {
    // * set Initial Query if provided
    if (this.query && this.formGroup) {
      this.formGroup
        .get('query_text')
        ?.setValue(this.query, { emitEvent: false });
    }
  }

  onSearchChanged(term: string, group: SearchGroup) {
    const term$ = (group as any)._term$ as BehaviorSubject<string>;
    if (term$) {
      term$.next(term);
    }
  }

  toggleSearchGroup(searchGroup: SearchGroup): void {
    const list =
      searchGroup.type === 'term'
        ? this.termSearchGroups
        : this.filterSearchGroups;

    const index = list.findIndex(
      (group) =>
        group.joinOption.propertyUri === searchGroup.joinOption.propertyUri &&
        group.joinOption.joinType === searchGroup.joinOption.joinType,
    );

    if (searchGroup.joinOption.selected) {
      if (index !== -1) {
        this.removeSearchGroup(searchGroup.type, searchGroup.joinOption);
      }
    } else {
      if (searchGroup.type === 'term') {
        this.addTermSearchGroup(searchGroup.joinOption);
      } else {
        this.addFilterSearchGroup(searchGroup.joinOption);
      }
    }
  }

  /**
   * Removes a search group at the specified index.
   *
   * @param index - The index of the search group to remove.
   */
  removeSearchGroup(type: 'term' | 'filter', joinOption: JoinOption) {
    const targetList =
      type === 'term' ? this.termSearchGroups : this.filterSearchGroups;

    const index = targetList.findIndex(
      (group) =>
        group.joinOption.propertyUri === joinOption.propertyUri &&
        group.joinOption.joinType === joinOption.joinType,
    );
    const searchGroup = targetList[index];
    if (index === -1) {
      this.logger.warn(
        `Search group with propertyUri ${joinOption.propertyUri} and joinType ${joinOption.joinType} not found.`,
      );
      return;
    }
    targetList.splice(index, 1);

    // Remove the corresponding form control
    const formControlName = searchGroup.formControlName;
    if (formControlName) {
      if (this.formGroup.contains(formControlName)) {
        this.formGroup.removeControl(formControlName, { emitEvent: false });
      } else {
        this.logger.warn(
          `Form control ${formControlName} not found in form group.`,
        );
      }
    }
    // Remove the value from the search group
    searchGroup.value = [];
    // Currently selected → user wants to unselect it
    searchGroup.joinOption.selected = false;
    searchGroup.joinType = 'and'; // reset to default join type
    // Trigger form change to update the parent component
  }

  toggleSearchGroupSelection(
    type: 'term' | 'filter',
    joinOption: JoinOption,
  ): void {
    if (joinOption.selected === true) {
      // has to be explicitly false
      this.removeSearchGroup(type, joinOption);
    } else {
      if (type === 'term') {
        this.addTermSearchGroup(joinOption);
      } else {
        this.addFilterSearchGroup(joinOption);
      }
    }
    // Trigger form change to update the parent component
  }

  addSearchGroup(
    type: 'term' | 'filter',
    joinOption: JoinOption,
    values = null,
  ): void {
    const targetList =
      type === 'term' ? this.termSearchGroups : this.filterSearchGroups;

    const sourceList =
      type === 'term'
        ? this.availableTermSearchGroups
        : this.availableFilterSearchGroups;

    const formControlName = `filters[${joinOption.propertyUri}][${joinOption.joinType}]`;

    let searchGroup = targetList.find(
      (group) =>
        group.joinOption.propertyUri === joinOption.propertyUri &&
        group.joinOption.joinType === joinOption.joinType,
    );

    const availableOptions = sourceList.find(
      (group) =>
        group.joinOption.propertyUri === joinOption.propertyUri &&
        group.joinOption.joinType === joinOption.joinType,
    );

    joinOption.selected = true; // mark the option as selected
    if (availableOptions?.joinOption) {
      availableOptions.joinOption.selected = true;
    }

    if (searchGroup) {
      // Group exists → update its values
      searchGroup.items = values || [];
      searchGroup.value = values || [];
      searchGroup.formControlName = formControlName;

      this.addOrUpdateControl(
        formControlName,
        new FormControl(values),
        [searchGroup.required ? Validators.required : null].filter(Boolean),
      );
      return;
    }

    // If new group, create it from source list or from scratch
    searchGroup = sourceList.find(
      (group) =>
        group.joinOption.propertyUri === joinOption?.propertyUri &&
        group.joinOption.joinType === joinOption?.joinType,
    );

    if (!searchGroup) {
      searchGroup = {
        title: '',
        joinType: 'and',
        type,
        joinOption: joinOption,
        items: values,
        value: [],
        formControlName,
        required: joinOption.required || false,
      };
    } else {
      searchGroup.formControlName = formControlName;
      searchGroup.joinOption = joinOption;
      searchGroup.type = type;
      searchGroup.items = values || [];
      searchGroup.value = values || [];
    }

    targetList.push(searchGroup);

    this.addOrUpdateControl(
      formControlName,
      new FormControl(values),
      [searchGroup.required ? Validators.required : null].filter(Boolean),
    );

    this.formGroup
      .get([formControlName])!
      .valueChanges.pipe(
        distinctUntilChanged(),
        tap((value) => {
          searchGroup.value = value;
          if (value && value.length > 0 && this.builderType === 'builder') {
            setTimeout(() => this.buildAdvancedQuery(), 0);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private parseNestedKeys(obj: Record<string, any>): Record<string, any> {
    const result: any = {};

    for (const fullKey in obj) {
      const value = obj[fullKey];

      // Match the base key and the nested keys inside brackets
      const matches = fullKey.match(/^([^\[]+)((?:\[[^\]]+\])+)$/);

      if (!matches) {
        // Simple key-value pair
        result[fullKey] = value;
        continue;
      }

      const baseKey = matches[1];
      const nestedKeys = Array.from(fullKey.matchAll(/\[([^\]]+)\]/g)).map(
        (m) => m[1],
      );

      let current = result;

      // Ensure base key exists
      if (!current[baseKey]) {
        current[baseKey] = {};
      }

      current = current[baseKey];

      // Iterate through nested keys except the last one
      for (let i = 0; i < nestedKeys.length - 1; i++) {
        const key = nestedKeys[i];
        if (!current[key]) {
          current[key] = {};
        }
        current = current[key];
      }

      // Assign the value to the last nested key
      current[nestedKeys[nestedKeys.length - 1]] = value;
    }

    return result;
  }

  buildAdvancedQuery() {
    // This method should build the advanced query based on the search groups

    const formGroupvalue = this.formGroup.value;

    const parsedFilters = this.parseNestedKeys(formGroupvalue || {});

    this.feedApi
      .getSolrQueryFromContentFilters(parsedFilters as ContentFilters)
      .pipe(
        take(1),
        tap((query) => {
          this.logger.debug('Advanced query built:', query);
          this.formGroup.get('query_text')?.setValue(query, {
            emitEvent: false,
          });
          this.componentDataChange.emit(query); // Emit the updated query
          this.previewTrigger$.next();
        }),
        catchError((error) => {
          this.logger.error('Error building advanced query:', error);

          return of(''); // Return an empty string or handle the error as needed
        }),
      )
      .subscribe();
  }

  isJoinOptionSelected(
    type: 'term' | 'filter',
    joinOption: JoinOption,
  ): boolean {
    const list =
      type === 'term' ? this.termSearchGroups : this.filterSearchGroups;
    return list.some(
      (group) =>
        group.joinOption.propertyUri === joinOption.propertyUri &&
        group.joinOption.joinType === joinOption.joinType,
    );
  }

  addTermSearchGroup(joinOption: JoinOption, values = null): void {
    this.addSearchGroup('term', joinOption, values);
  }

  addFilterSearchGroup(joinOption: JoinOption, values = null): void {
    this.addSearchGroup('filter', joinOption, values);
  }

  // In your method that updates search terms

  compareFn(optionOne: JoinOption, optionTwo: JoinOption): boolean {
    return (
      optionOne &&
      optionTwo &&
      optionOne.joinType === optionTwo.joinType &&
      optionOne.propertyUri === optionTwo.propertyUri
    );
  }

  addOrUpdateControl(
    name: string,
    control: FormControl,
    validators: any[] = [],
  ) {
    control.setValidators(validators);
    control.updateValueAndValidity({ emitEvent: false });

    if (!this.formGroup.get([name])) {
      this.formGroup.addControl(name, control, { emitEvent: true });
    } else {
      this.formGroup.setControl(name, control, { emitEvent: true });
    }
  }

  /**
   * Builds and returns a FormGroup object for the search builder component.
   * The FormGroup contains form controls for various search parameters.
   *
   * @returns The FormGroup object.
   */
  buildFormGroup(): FormGroup {
    let formGroup = this.formGroup;

    formGroup.get('query_created_from').setValue(this.builderType, {
      emitEvent: false,
    });
    formGroup.get('object_type_id').setValue(this.objectTypeId, {
      emitEvent: false,
    });
    formGroup.get('combined_feeds').setValue(null, { emitEvent: false });
    // formGroup.get('query_text').setValue('', {
    //   emitEvent: false,
    // });

    return formGroup;
  }

  changeBuilderType(type: BuilderType) {
    this.builderType = type;
    this.builderChanged.emit(type);
    this.formGroup.get('query_created_from').setValue(this.builderType);

    if (type === 'excluded-posts') {
      this.sidebarService.hideButtons(true);
    } else {
      this.sidebarService.showButtons();
    }

    this.updateFieldValidatorsByBuilderType(type);
  }

  private updateFieldValidatorsByBuilderType(type: BuilderType) {
    let requiredFieldsByBuilderType: any = {};

    // Add query builder requirements if feed is not Clipping
    if (
      this.objectTypeId !== NomObjectTypes.CLIPPING_FEED &&
      this.objectTypeId !== NomObjectTypes.STORY_FEED
    ) {
      requiredFieldsByBuilderType = {
        editor: ['query_text'],
        builder: ['filters[post_content_search][include]'],
      };
    }

    if (
      this.objectTypeId === NomObjectTypes.SOCIAL_FEED ||
      (this.feed && this.feed.object_type_id === NomObjectTypes.SOCIAL_FEED)
    ) {
      requiredFieldsByBuilderType?.builder.push('post_social_source_id');
      requiredFieldsByBuilderType?.editor.push('post_social_source_id');
    }

    // Set validators based on the builder type
    const requiredFields = requiredFieldsByBuilderType?.[type] || [];
    const controls = this.formGroup.controls;
    Object.keys(controls).forEach((key) => {
      const control = controls[key];
      if (requiredFields.includes(key)) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * query-editor codemirror
   * Updates the query string and triggers the form change event.
   *
   * @param query The new query string.
   */
  onQueryChange(query: string) {
    this.logger.debug('Query changed:', query);
    this.componentDataChange.emit(query); // Emit the updated query
  }

  // In editor mode we trigger preview on blur (instead of per-keystroke) so that
  // we don't fire requests while the user is mid-edit with a partial/invalid query.
  onQueryBlur(): void {
    if (this.builderType !== 'editor') return;
    this.previewTrigger$.next();
  }

  isFilterSearchGroupAvailable(searchGroup: SearchGroup): boolean {
    return (
      (this.objectTypeId === NomObjectTypes.SOCIAL_FEED ||
        this.builderType === 'builder') &&
      (!searchGroup.objectTypeIds ||
        searchGroup.objectTypeIds.includes(this.objectTypeId))
    );
  }

  setQuery(query: string) {
    this.queryEditor.setQuery(query);
  }

  addHint(hint: string) {
    this.queryEditor.addOperator(hint);
  }

  openHints() {
    this.queryHintsOverlayService.open(this.componentId);
  }

  closeHints() {
    this.queryHintsOverlayService.closeIfOwner(this.componentId);
  }

  historyRecordClick(item: QueryHistoryRecord) {
    this.changeBuilderType('editor');
    this.formGroup.get('query_text').setValue(item.query, {
      emitEvent: true,
    });
  }

  getPreviewStatus(): 'loading' | 'results' | 'empty' | 'idle' {
    if (this.postsLoading) return 'loading';
    if (!this.postLoaded) return 'idle';
    if (this.postsPreviewCount && this.postsPreviewCount > 0) return 'results';
    return 'empty';
  }

  previewQuery() {
    let queryText = this.formGroup.get('query_text')?.value;
    let combinedFeedIds = this.formGroup.get('combined_feeds')?.value;
    let contentPlatformIds = this.formGroup.get('post_social_source_id')?.value;

    if (!Array.isArray(combinedFeedIds)) {
      combinedFeedIds = [];
    }
    if (this.objectTypeId === NomObjectTypes.COMBINED_FEED) {
      if (this.combinedFeedIds.size > 0) {
        combinedFeedIds = Array.from(this.combinedFeedIds.keys());
        queryText = '';
      } else {
        this.toastService.showError(
          this.translateService.instant(
            'search-builder.message.no-feeds-selected',
          ),
        );
        this.posts = [];
        return;
      }
    } else {
      if (!queryText) {
        this.posts = [];
        return;
      }
    }

    if (this.objectTypeId === NomObjectTypes.SOCIAL_FEED) {
    }
    ``;

    this.postsLoading = true;
    this.posts = [];

    this.feedApi
      .fetchLiveData(
        queryText,
        this.objectTypeId,
        combinedFeedIds,
        contentPlatformIds,
      )
      .pipe(
        tap((response) => {
          this.postLoaded = true;
          this.posts = response.posts;
          this.postsPreviewCount = response.count_posts;
          this.logger.debug('Fetched posts:', response);
        }),
        catchError((err) => {
          console.error('Error fetching posts:', err);
          return of([]);
        }),
        finalize(() => {
          this.postsLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  trackByValue(index: number, item: JoinOption): any {
    return item.propertyUri + item.joinType;
  }

  onSocialSourceChange(value: unknown, checked: boolean) {
    const formArray: FormArray = this.formGroup.get(
      'post_social_source_id',
    ) as FormArray;
    /* Selected */
    if (checked) {
      // Add a new control in the arrayForm
      formArray.push(new FormControl(value));
    } else {
      /* unselected */
      // find the unselected element
      let i: number = 0;

      formArray.controls.forEach((ctrl: FormControl) => {
        if (ctrl.value == value) {
          // Remove the unselected element from the arrayForm
          formArray.removeAt(i);
          return;
        }

        i++;
      });
    }
  }
  onSubmitSuccess(formValue: any): void {
    this.queryEditor?.clearEditorErrors();
  }
  onSubmitError(error: any): void {
    if (error && error.type === 'validation') {
      this.queryEditor?.displayEditorError(
        error.line,
        error.message,
        error.column,
      );
    } else {
      this.logger.error(
        'SearchBuilderComponent: Form submission failed with error:',
        error,
      );
      this.toastService.showError(
        this.translateService.instant(
          'search-builder.error.form-submit-failed',
        ),
      );
    }
  }
  onObjectCreated(object: any): void {
    this.logger.info(
      'SearchBuilderComponent: Object created successfully:',
      object,
    );
  }
  onObjectUpdated(object: any): void {
    this.logger.info(
      'SearchBuilderComponent: Object updated successfully:',
      object,
    );
  }

  onCombinedFeedSelectionChange(
    combinedFeedIds: Map<string, ObjectDefinition>,
  ): void {
    this.combinedFeedIds = combinedFeedIds;
    this.formGroup
      .get('combined_feeds')
      ?.setValue(Array.from(combinedFeedIds.keys()), { emitEvent: true });
  }

  ngOnDestroy() {
    this.closeHints();
    this.sidebarService.showButtons();
    this.subscription.unsubscribe();
  }
}
